import React, { useState, useCallback } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useError } from "../../../contexts/ErrorContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useStudents, StudentRollScholarUpdateDto } from "../../../hooks/useStudents";
import RollNumberModal from "../../../components/Modals/RollNumberModal";
import AddIcon from "@mui/icons-material/Add";

const RollNumberManagement: React.FC = () => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useError();
  const { user, isTeacher } = useAuth();
  const [rollNumberModalOpen, setRollNumberModalOpen] = useState(false);

  // Get schoolId
  const schoolId = user?.schoolId;

  const { updateRollNumberAndScholarNumberMutation } = useStudents(false, schoolId || undefined);

  const handleOpenRollNumberModal = useCallback(() => {
    setRollNumberModalOpen(true);
  }, []);

  const handleCloseRollNumberModal = useCallback(() => {
    setRollNumberModalOpen(false);
  }, []);

  const handleSubmitRollNumbers = useCallback(
    async (students: StudentRollScholarUpdateDto[]) => {
      try {
        await updateRollNumberAndScholarNumberMutation.mutateAsync(students);
        showSuccess(
          t("roll_numbers_updated_successfully") || "Roll numbers updated successfully"
        );
        handleCloseRollNumberModal();
      } catch (error: any) {
        showError(
          error?.response?.data?.message ||
            t("error_updating_roll_numbers") ||
            "Error updating roll numbers"
        );
      }
    },
    [updateRollNumberAndScholarNumberMutation, handleCloseRollNumberModal, showError, showSuccess, t]
  );

  return (
    <>
      <Paper sx={{ p: 2, maxWidth: 1500, mx: "auto", mt: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            {t("roll_number_management") || "Roll Number Management"}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: "#3092e3",
              "&:hover": { backgroundColor: "primary.main" },
            }}
            onClick={handleOpenRollNumberModal}
          >
            {t("add_roll_number") || "Add Roll Number"}
          </Button>
        </Box>
      </Paper>

      <RollNumberModal
        open={rollNumberModalOpen}
        handleClose={handleCloseRollNumberModal}
        onSubmit={handleSubmitRollNumbers}
      />
    </>
  );
};

export default RollNumberManagement;

